import type { ColorTokenKey } from 'smoothie';

import type { TransformedPaycheckRoutingConfig } from '../../state/PaycheckRoutingAtoms';
import { type BankAccountDisplayDetail } from '../../types/BankAccount';
import {
    PaycheckRoutingConfigStatus,
    type PaycheckRoutingIncomeDetail,
    type PaycheckRoutingRule,
    PaycheckRoutingStatus,
} from '../../types/PaycheckRouting';

export const getConfigStatusForPaycheck = (
    paycheck: PaycheckRoutingIncomeDetail,
    routingConfigs: TransformedPaycheckRoutingConfig[],
    closedAccountDetails: BankAccountDisplayDetail[] = [],
): {
    status: PaycheckRoutingConfigStatus;
    color: ColorTokenKey;
    needsAttention: boolean;
    backgroundColor: ColorTokenKey;
} => {
    const config = routingConfigs.find(
        (paycheckRoutingConfig) =>
            paycheckRoutingConfig.accountId === paycheck.accountId &&
            paycheckRoutingConfig.originator === paycheck.originator &&
            paycheckRoutingConfig.receiver === paycheck.receiver,
    );

    const needsAttention =
        getClosedAccountsInConfig({ config, closedAccountDetails }).length > 0;

    if (config?.status === PaycheckRoutingStatus.ACTIVE) {
        return {
            status: PaycheckRoutingConfigStatus.ENABLED,
            color: '$green1000',
            backgroundColor: '$green300',
            needsAttention,
        };
    }

    if (config?.status === PaycheckRoutingStatus.INACTIVE) {
        return {
            status: PaycheckRoutingConfigStatus.DISABLED,
            color: '$green1000',
            backgroundColor: '$green300',
            needsAttention,
        };
    }

    return {
        status: PaycheckRoutingConfigStatus.ELIGIBLE,
        color: '$gray1100',
        backgroundColor: '$gray200',
        needsAttention,
    };
};

export const getClosedAccountsInConfig = ({
    config,
    closedAccountDetails,
}: {
    config?: Partial<TransformedPaycheckRoutingConfig>;
    closedAccountDetails: BankAccountDisplayDetail[];
}) => {
    if (config == null) {
        return [];
    }

    const ruleBankAccountIds = [
        ...(config?.rules?.primary ?? []),
        ...(config?.rules?.remaining ?? []),
    ].map((rule) => rule.accountId);

    return closedAccountDetails.filter((account) =>
        ruleBankAccountIds.includes(account.bankAccountId),
    );
};

export const isRoutingRuleBroken = ({
    rule,
    closedAccountDetails,
}: {
    rule: PaycheckRoutingRule;
    closedAccountDetails: BankAccountDisplayDetail[];
}) => {
    return closedAccountDetails
        .map((account) => account.bankAccountId)
        .includes(rule.accountId);
};
export const paycheckMatchesRoutingConfig = (
    paycheck: PaycheckRoutingIncomeDetail,
    routingConfig: TransformedPaycheckRoutingConfig,
) => {
    return (
        paycheck.accountId === routingConfig.accountId &&
        paycheck.originator === routingConfig.originator &&
        paycheck.receiver === routingConfig.receiver
    );
};

export const findMatchingRoutingConfigForPaycheck = (
    paycheck: PaycheckRoutingIncomeDetail,
    routingConfigs: TransformedPaycheckRoutingConfig[],
) => {
    return routingConfigs.find((routingConfig) =>
        paycheckMatchesRoutingConfig(paycheck, routingConfig),
    );
};

export const paycheckHasActiveRoutingConfig = (
    paycheck: PaycheckRoutingIncomeDetail,
    routingConfigs: TransformedPaycheckRoutingConfig[],
): boolean => {
    return !!routingConfigs.find(
        (routingConfig) =>
            routingConfig.isActive &&
            paycheckMatchesRoutingConfig(paycheck, routingConfig),
    );
};
